<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { Calculator, FilePlus2, Pencil } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { formatDisplayDate } from '@yuksalish/ui'
import PaginationControls from '../../components/PaginationControls.vue'
import {
  getProduct,
  getBomVersion,
  getProductUoms,
  listProductBomVersions,
  listTechnicalPassports,
} from '../productService'
import {
  formatIdentifier,
  isManufacturedProduct,
  resolveProductImage,
} from '../productPresentation'
import type {
  BomVersion,
  BomVersionSummary,
  EngineeringProduct,
  ProductUom,
  TechnicalPassport,
} from '../types'

const route = useRoute()
const client = useApiClient()
const auth = useAuthStore()
const product = ref<EngineeringProduct | null>(null)
const bomVersions = ref<BomVersionSummary[]>([])
const activeBom = ref<BomVersion | null>(null)
const technicalPassports = ref<TechnicalPassport[]>([])
const productUoms = ref<ProductUom[]>([])
const loading = ref(false)
const bomLoading = ref(false)
const errorMessage = ref('')
const bomError = ref('')
const passportLoading = ref(false)
const passportError = ref('')
const bomPage = ref(1)
const bomPageSize = ref(5)
const productId = computed(() => Number(route.params.id))
const hasValidId = computed(
  () => Number.isInteger(productId.value) && productId.value > 0,
)
const productImage = computed(() =>
  resolveProductImage(product.value?.photo_url ?? null),
)
const canViewBom = computed(
  () => auth.user?.permissions.includes('bom.view') ?? false,
)
const canManageBom = computed(
  () => auth.user?.permissions.includes('bom.manage') ?? false,
)
const canViewPassport = computed(
  () => auth.user?.permissions.includes('production_orders.view') ?? false,
)
const canManagePassport = computed(
  () => auth.user?.permissions.includes('production_orders.manage') ?? false,
)
const canManageProduct = computed(
  () => auth.user?.permissions.includes('products.update') ?? false,
)
const materialNamesById = computed(() => {
  const names = new Map<number, string>()

  activeBom.value?.operations.forEach((operation) => {
    operation.materials.forEach((material) => {
      if (material.material_id && material.material_name?.trim()) {
        names.set(material.material_id, material.material_name.trim())
      }
    })
  })

  technicalPassports.value.forEach((passport) => {
    passport.raw_materials?.forEach((material) => {
      if (material.material_product_id && material.material_name_snapshot?.trim()) {
        names.set(material.material_product_id, material.material_name_snapshot.trim())
      }
    })
  })

  return names
})
const bomLines = computed(() =>
  activeBom.value?.operations.flatMap((operation) =>
    operation.materials.map((material) => ({ operation, material })),
  ) ?? [],
)
const bomTotalPages = computed(() =>
  Math.max(1, Math.ceil(bomLines.value.length / bomPageSize.value)),
)
const paginatedBomLines = computed(() => {
  const start = (bomPage.value - 1) * bomPageSize.value
  return bomLines.value.slice(start, start + bomPageSize.value)
})
const readinessRequirements = computed(() =>
  (product.value?.production_readiness?.missing_requirements ?? []).map((requirement) =>
    requirement
      .replace(/\bMaterial\s*#(\d+)\b/gi, (_, id) => materialNamesById.value.get(Number(id)) ?? `Material #${id}`)
      .replace(/\bmaterial_id\s*(\d+)\b/gi, (_, id) => materialNamesById.value.get(Number(id)) ?? `material_id ${id}`)
      .replace(/\bmaterial id\s*(\d+)\b/gi, (_, id) => materialNamesById.value.get(Number(id)) ?? `material ID ${id}`)
      .replace(/\braw material\s*#(\d+)\b/gi, (_, id) => materialNamesById.value.get(Number(id)) ?? `Raw material #${id}`),
  ),
)

async function loadBom(): Promise<void> {
  if (!canViewBom.value || !hasValidId.value) {
    return
  }
  bomLoading.value = true
  bomError.value = ''
  try {
    bomVersions.value = await listProductBomVersions(client, productId.value)
    const requestedVersionId = Number(route.query.bom)
    const selectedVersion =
      bomVersions.value.find((version) => version.id === requestedVersionId) ??
      bomVersions.value.find((version) => Boolean(version.is_active)) ??
      bomVersions.value[0]
    activeBom.value = selectedVersion
      ? await getBomVersion(client, selectedVersion.id)
      : null
  } catch (error: unknown) {
    bomVersions.value = []
    activeBom.value = null
    bomError.value = formatApiError(error)
  } finally {
    bomLoading.value = false
  }
}

async function selectBomVersion(version: BomVersionSummary): Promise<void> {
  if (activeBom.value?.id === version.id) return
  bomLoading.value = true
  bomError.value = ''
  try {
    activeBom.value = await getBomVersion(client, version.id)
    bomPage.value = 1
  } catch (error: unknown) {
    bomError.value = formatApiError(error)
  } finally {
    bomLoading.value = false
  }
}

async function loadTechnicalPassports(): Promise<void> {
  if (!canViewPassport.value || !hasValidId.value) {
    technicalPassports.value = []
    return
  }
  passportLoading.value = true
  passportError.value = ''
  try {
    technicalPassports.value = await listTechnicalPassports(
      client,
      productId.value,
    )
  } catch (error: unknown) {
    technicalPassports.value = []
    passportError.value = formatApiError(error)
  } finally {
    passportLoading.value = false
  }
}

async function loadProduct(): Promise<void> {
  if (!hasValidId.value) {
    product.value = null
    errorMessage.value = 'The product identifier is invalid.'
    return
  }

  loading.value = true
  errorMessage.value = ''
  try {
    const [result, uoms] = await Promise.all([
      getProduct(client, productId.value),
      getProductUoms(client, productId.value),
    ])
    if (!isManufacturedProduct(result)) {
      product.value = null
      errorMessage.value =
        'This record is not a manufactured product. Open it from its catalog section.'
      return
    }
    product.value = result
    productUoms.value = uoms
    await Promise.all([loadBom(), loadTechnicalPassports()])
  } catch (error: unknown) {
    product.value = null
    productUoms.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

onMounted(loadProduct)
watch(productId, loadProduct)
watch([bomLines, bomPageSize], () => {
  bomPage.value = Math.min(bomPage.value, bomTotalPages.value)
})
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :data-tooltip="$t('engineering.action.backProducts')" to="/products">{{ $t('engineering.action.backProducts') }}</RouterLink>
  </div>

  <div v-if="loading" class="detail-state">{{ $t('engineering.state.loadingProduct') }}</div>
  <div v-else-if="errorMessage" class="detail-state detail-state--error">
    <p role="alert">{{ errorMessage }}</p>
    <button v-if="hasValidId" type="button" @click="loadProduct">{{ $t('engineering.action.retry') }}</button>
  </div>

  <template v-else-if="product">
    <div class="page-heading">
      <div>
        <p class="eyebrow">{{ product.sku }}</p>
        <h2>{{ product.name }}</h2>
        <p>{{ product.group?.name || product.type }}</p>
      </div>
      <div class="heading-actions">
        <RouterLink
          v-if="canManageProduct"
          class="edit-link"
          :to="`/products/${product.id}/edit`"
        >
          <Pencil :size="16" aria-hidden="true" />
          {{ $t('engineering.productForm.editAction.manufactured') }}
        </RouterLink>
        <span
          v-else
          class="edit-link edit-link--disabled"
          :title="$t('engineering.productForm.permissionHint')"
          :aria-label="$t('engineering.productForm.permissionHint')"
        >
          <Pencil :size="16" aria-hidden="true" />
          {{ $t('engineering.productForm.editAction.manufactured') }}
        </span>
        <RouterLink
          v-if="canViewPassport"
          class="edit-link"
          :to="`/costing?product_id=${product.id}`"
        >
          <Calculator :size="16" aria-hidden="true" />
          {{ $t('engineering.nav.costing') }}
        </RouterLink>
        <span
          class="readiness"
          :class="product.production_readiness?.is_ready
            ? 'readiness--ready'
            : 'readiness--blocked'"
        >
          {{ product.production_readiness?.is_ready ? $t('engineering.product.ready') : $t('engineering.product.notReady') }}
        </span>
      </div>
    </div>

    <div class="detail-layout">
      <section class="identity-section">
        <div class="product-media">
          <img
            v-if="productImage"
            :src="productImage"
            :alt="product.name"
          >
          <div v-else class="image-placeholder" aria-hidden="true">
            {{ product.name.slice(0, 1).toUpperCase() }}
          </div>
        </div>
        <div>
          <h3>{{ $t('engineering.product.definition') }}</h3>
          <dl class="detail-grid">
            <div><dt>{{ $t('engineering.product.internalSku') }}</dt><dd>{{ formatIdentifier(product.internal_sku_code) }}</dd></div>
            <div><dt>{{ $t('engineering.product.barcode') }}</dt><dd>{{ formatIdentifier(product.ean13) }}</dd></div>
            <div><dt>{{ $t('engineering.product.type') }}</dt><dd>{{ product.type }}</dd></div>
            <div><dt>{{ $t('engineering.product.audience') }}</dt><dd>{{ formatIdentifier(product.audience?.name || product.audience_segment) }}</dd></div>
            <div><dt>{{ $t('engineering.product.season') }}</dt><dd>{{ formatIdentifier(product.season_info?.name || product.season) }}</dd></div>
            <div><dt>{{ $t('engineering.product.unit') }}</dt><dd>{{ product.unit }}</dd></div>
            <div><dt>{{ $t('engineering.product.uoms') }}</dt><dd>{{ productUoms.length || $t('engineering.product.notSet') }}</dd></div>
            <div><dt>{{ $t('engineering.product.minimumOrder') }}</dt><dd>{{ product.min_order_quantity ?? $t('engineering.product.notSet') }}</dd></div>
            <div><dt>{{ $t('engineering.product.pack') }}</dt><dd>{{ product.socks_per_pack ?? "-" }} / {{ product.packs_per_bag ?? "-" }}</dd></div>
          </dl>
        </div>
      </section>

      <section class="uom-section">
        <div class="section-heading">
          <div>
            <h3>{{ $t('engineering.product.uoms') }}</h3>
            <p>{{ $t('engineering.product.uomsHelp') }}</p>
          </div>
        </div>
        <div v-if="productUoms.length" class="bom-table">
          <table>
            <thead>
              <tr>
                <th>{{ $t('engineering.product.uomCode') }}</th>
                <th>{{ $t('engineering.product.uomLabel') }}</th>
                <th>{{ $t('engineering.product.uomFactor') }}</th>
                <th>{{ $t('engineering.product.uomEntry') }}</th>
                <th>{{ $t('engineering.product.uomStatus') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="uom in productUoms" :key="uom.uom_code">
                <td><strong>{{ uom.uom_code }}</strong></td>
                <td>{{ uom.label }}</td>
                <td>{{ uom.to_base_factor }}</td>
                <td>{{ uom.is_fractional_allowed ? $t('engineering.product.fractionalAllowed') : $t('engineering.product.wholeOnly') }}</td>
                <td>
                  <span v-if="uom.is_base">{{ $t('engineering.product.baseUom') }}</span>
                  <span v-else>{{ uom.is_active === false || uom.is_active === 0 ? $t('engineering.product.inactive') : $t('engineering.product.activeAlt') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="section-state">
          {{ $t('engineering.product.uomsEmpty') }}
        </div>
      </section>

      <section class="readiness-section">
        <div class="section-heading">
          <div>
            <h3>{{ $t('engineering.product.readiness') }}</h3>
            <p>{{ $t('engineering.product.readinessHelp') }}</p>
          </div>
          <RouterLink
            v-if="
              canViewPassport &&
              product.production_readiness?.technical_passport_id
            "
            :to="`/technical-passports/${product.production_readiness.technical_passport_id}`"
          >
            {{ $t('engineering.passport.label', { id: product.production_readiness.technical_passport_id }) }}
          </RouterLink>
          <span v-else-if="product.production_readiness?.technical_passport_id">
            {{ $t('engineering.passport.label', { id: product.production_readiness.technical_passport_id }) }}
          </span>
        </div>

        <div
          v-if="product.production_readiness?.is_ready"
          class="ready-message"
        >
          {{ $t('engineering.product.readyMessage') }}
        </div>
        <ol v-else class="requirement-list">
          <li
            v-for="requirement in readinessRequirements"
            :key="requirement"
          >
            <span aria-hidden="true">!</span>
            <div>
              <strong>{{ $t('engineering.product.missing') }}</strong>
              <p>{{ requirement }}</p>
            </div>
          </li>
        </ol>
      </section>
    </div>

    <section>
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.passport.title') }}</h3>
          <p>{{ $t('engineering.passport.help') }}</p>
        </div>
        <RouterLink
          v-if="canManagePassport"
          class="detail-action"
          :to="`/technical-passports/new?product_id=${product.id}`"
        ><FilePlus2 :size="16" aria-hidden="true" />{{ $t('engineering.passport.create') }}</RouterLink>
        <span v-else-if="canViewPassport">{{ $t('engineering.passport.records', { count: technicalPassports.length }) }}</span>
      </div>
      <div v-if="!canViewPassport" class="permission-notice">
        {{ $t('engineering.passport.permission', { permission: 'production_orders.view' }) }}
      </div>
      <div v-else-if="passportLoading" class="section-state">
        {{ $t('engineering.passport.loading') }}
      </div>
      <div v-else-if="passportError" class="section-error" role="alert">
        <span>{{ passportError }}</span>
        <button type="button" @click="loadTechnicalPassports">{{ $t('engineering.action.retry') }}</button>
      </div>
      <div v-else-if="!technicalPassports.length" class="section-state">
        {{ $t('engineering.passport.empty') }}
      </div>
      <div v-else class="passport-list">
        <RouterLink
          v-for="item in technicalPassports"
          :key="item.id"
          :to="`/technical-passports/${item.id}`"
        >
          <span>
            <strong>{{ $t('engineering.passport.label', { id: item.id }) }}</strong>
            <small>{{ formatDisplayDate(item.updated_at || item.created_at) || $t('engineering.passport.dateUnavailable') }}</small>
          </span>
          <span class="passport-status">{{ $t(`engineering.passport.status.${item.status}`) }}</span>
        </RouterLink>
      </div>
    </section>

    <section>
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.product.bomTitle') }}</h3>
          <p>{{ $t('engineering.product.bomHelp') }}</p>
        </div>
        <div v-if="canViewBom" class="bom-heading-actions">
          <span>{{ $t('engineering.product.materials', { count: bomLines.length }) }} · {{ $t('engineering.product.operations', { count: activeBom?.operations.length ?? 0 }) }}</span>
          <RouterLink v-if="canManageBom" class="detail-action" :to="`/products/${product.id}/bom/new`"><FilePlus2 :size="16" aria-hidden="true" />{{ $t('engineering.action.createBom') }}</RouterLink>
          <RouterLink v-if="canManageBom && activeBom" :to="`/products/${product.id}/bom/${activeBom.id}/edit`">{{ $t('engineering.action.revise', { version: activeBom.version_number }) }}</RouterLink>
        </div>
      </div>

      <div v-if="!canViewBom" class="permission-notice">
        BOM details require the <code>bom.view</code> permission.
      </div>
      <div v-else-if="bomLoading" class="section-state">{{ $t('engineering.state.loadingBom') }}</div>
      <div v-else-if="bomError" class="section-error" role="alert">
        <span>{{ bomError }}</span>
        <button type="button" @click="loadBom">{{ $t('engineering.action.retry') }}</button>
      </div>
      <div v-else-if="!bomVersions.length" class="section-state">
        {{ $t('engineering.state.noBom') }}
      </div>
      <div v-else>
        <div class="version-list" aria-label="BOM version history">
          <button v-for="version in bomVersions" :key="version.id" type="button" class="version-row" :class="{ 'version-row--selected': activeBom?.id === version.id }" @click="selectBomVersion(version)">
            <span><strong>{{ $t('engineering.product.version', { version: version.version_number }) }}</strong><small>{{ formatDisplayDate(version.created_at) || '—' }}</small></span>
            <span>{{ $t('engineering.product.operations', { count: version.operations_count }) }} · {{ $t('engineering.product.materials', { count: version.materials_count }) }}</span>
            <span class="version-state" :class="version.is_active ? 'version-state--active' : ''">
              {{ version.is_active ? $t('engineering.product.active') : $t('engineering.product.superseded') }}
            </span>
          </button>
        </div>
      <div v-if="activeBom && bomLines.length" class="bom-layout">
        <ol class="operation-list">
          <li v-for="operation in activeBom.operations" :key="operation.id">
            <span>{{ operation.operation_sequence }}</span>
            <div>
              <strong>{{ operation.operation_name }}</strong>
              <p>{{ operation.department_name || $t('engineering.product.departmentMissing') }} · {{ $t('engineering.product.materials', { count: operation.materials.length }) }}</p>
            </div>
          </li>
        </ol>
        <div class="bom-table">
          <table>
            <thead>
              <tr>
                <th>{{ $t('engineering.product.material') }}</th>
                <th>{{ $t('engineering.product.operation') }}</th>
                <th>{{ $t('engineering.product.department') }}</th>
                <th>{{ $t('engineering.product.quantity') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in paginatedBomLines" :key="line.material.id">
                <td><strong>{{ line.material.material_name }}</strong><span>{{ line.material.material_sku }}</span></td>
                <td>#{{ line.operation.operation_sequence }} {{ line.operation.operation_name }}</td>
                <td>{{ line.operation.department_name || '-' }}</td>
                <td>{{ line.material.quantity }} {{ line.material.material_unit }}</td>
              </tr>
            </tbody>
          </table>
          <PaginationControls
            v-model:page="bomPage"
            v-model:page-size="bomPageSize"
            :total="bomLines.length"
          />
        </div>
      </div>
      <div v-else class="section-state">{{ $t('engineering.state.noMaterials') }}</div>
      </div>
    </section>

    <section>
      <div class="section-heading">
        <h3>Tags and commercial references</h3>
        <span>{{ product.prices.length }} prices</span>
      </div>
      <div class="tag-list">
        <span v-for="tag in product.tags" :key="tag">{{ tag }}</span>
        <p v-if="!product.tags.length">No tags assigned.</p>
      </div>
    </section>
  </template>
</template>

<style scoped>
.back-link {
  display: inline-block;
  margin-bottom: 20px;
  color: #285b9a;
  font-size: 13px;
  font-weight: 700;
}

.page-heading {
  display: flex;
  gap: 24px;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 24px;
}

.heading-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.eyebrow {
  margin: 0 0 5px;
  color: #66726e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 26px;
  letter-spacing: 0;
}

.page-heading p:last-child {
  margin: 7px 0 0;
  color: #66726e;
}

.readiness {
  padding: 7px 10px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}

.readiness--ready {
  border-color: #86b4a4;
  color: #236451;
  background: #eff8f5;
}

.readiness--blocked {
  border-color: #d2b37c;
  color: #765017;
  background: #fff8e9;
}

.edit-link {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 9px 12px;
  border: 1px solid #3568a8;
  border-radius: 8px;
  color: #285b9a;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.detail-action {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 7px 10px;
  border: 1px solid #3568a8;
  border-radius: 8px;
  color: #285b9a;
  background: #ffffff;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
}

.edit-link--disabled {
  border-color: #b9c5c1;
  color: #6f7d78;
  background: #eef2f1;
  cursor: not-allowed;
}

.detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(300px, 0.8fr);
  gap: 16px;
}

section {
  margin-bottom: 16px;
  padding: 22px;
  border: 1px solid #d7dfdc;
  background: #ffffff;
}

.identity-section {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 22px;
}

.product-media {
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid #d7dfdc;
  background: #f1f4f3;
}

.product-media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-placeholder {
  display: grid;
  width: 100%;
  height: 100%;
  color: #ffffff;
  background: #3568a8;
  font-size: 52px;
  font-weight: 700;
  place-items: center;
}

h3 {
  margin: 0 0 18px;
  font-size: 16px;
  letter-spacing: 0;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin: 0;
}

dt {
  color: #66726e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

dd {
  margin: 5px 0 0;
  overflow-wrap: anywhere;
}

.section-heading {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  justify-content: space-between;
}

.section-heading h3 {
  margin-bottom: 4px;
}

.section-heading p {
  margin: 0;
  color: #66726e;
  font-size: 13px;
}

.section-heading > span {
  color: #53625d;
  font-size: 12px;
  white-space: nowrap;
}

.section-heading > a {
  color: #285b9a;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.bom-heading-actions {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #53625d;
  font-size: 12px;
}

.bom-heading-actions a {
  padding: 7px 10px;
  border: 1px solid #b9c5c1;
  color: #285b9a;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.permission-notice,
.section-state {
  margin-top: 16px;
  padding: 16px;
  color: #66726e;
  background: #f3f6f5;
}

.section-error {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #d7a8a1;
  color: #852d22;
  background: #fff5f3;
}

.passport-list {
  display: grid;
  gap: 8px;
  margin-top: 16px;
}

.passport-list a {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius:10px;
  border: 1px solid #f4f08c;
  color: #24322d;
  background-color: #fff693;
  text-decoration: none;
}

.passport-list a:hover {
  border-color: #f0df7f;
  background: #d9e275;
}

.passport-list a > span:first-child {
  display: grid;
  gap: 4px;
}

.passport-list small {
  color: #66726e;
}

.passport-status {
  color: #53625d;
  font-size: 12px;
  text-transform: capitalize;
  white-space: nowrap;
}

.bom-layout {
  display: grid;
  grid-template-columns: minmax(230px, 0.35fr) minmax(0, 1fr);
  gap: 18px;
  margin-top: 18px;
}

.version-list {
  display: grid;
  gap: 7px;
  margin-top: 18px;
}

.version-row {
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(180px, 1fr) auto;
  gap: 14px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #d7dfdc;
  color: #24322d;
  background: #ffffff;
  font-size: 13px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.version-row:hover,
.version-row--selected {
  border-color: #7899c3;
  background: #f4f7fb;
}

.version-row--selected {
  box-shadow: inset 3px 0 #3568a8;
}

.version-row > span:first-child {
  display: grid;
  gap: 3px;
}

.version-row small,
.version-state {
  color: #66726e;
  font-size: 12px;
}

.version-state {
  font-weight: 700;
}

.version-state--active {
  color: #236451;
}

.operation-list {
  display: grid;
  align-content: start;
  gap: 7px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.operation-list li {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  gap: 10px;
  padding: 10px;
  border: 1px solid #d7dfdc;
}

.operation-list li > span {
  display: grid;
  width: 28px;
  height: 28px;
  color: #ffffff;
  background: #3568a8;
  font-size: 12px;
  font-weight: 700;
  place-items: center;
}

.operation-list p {
  margin: 3px 0 0;
  color: #66726e;
  font-size: 12px;
}

.bom-table {
  overflow-x: auto;
}

.bom-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.bom-table th,
.bom-table td {
  padding: 10px;
  border: 1px solid #e1e6e4;
  text-align: left;
}

.bom-table th {
  color: #586660;
  background: #f3f6f5;
  font-size: 11px;
  text-transform: uppercase;
}

.bom-table td span {
  display: block;
  margin-top: 3px;
  color: #71807a;
  font-size: 11px;
}

.ready-message {
  padding: 14px;
  border-left: 3px solid #358267;
  color: #236451;
  background: #eff8f5;
}

.requirement-list {
  display: grid;
  gap: 8px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

.requirement-list li {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  gap: 10px;
  padding: 11px;
  border: 1px solid #ead6ad;
  background: #fffaf0;
}

.requirement-list li > span {
  display: grid;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #ffffff;
  background: #a46c1d;
  font-weight: 700;
  place-items: center;
}

.requirement-list strong {
  font-size: 12px;
}

.requirement-list p {
  margin: 3px 0 0;
  color: #6c5734;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.tag-list span {
  padding: 4px 7px;
  border: 1px solid #bcc9c4;
  border-radius: 8px;
  font-size: 12px;
}

.tag-list p {
  margin: 0;
  color: #66726e;
}

.detail-state {
  display: grid;
  min-height: 240px;
  color: #697671;
  place-items: center;
}

.detail-state--error {
  align-content: center;
  gap: 12px;
  color: #852d22;
}

@media (max-width: 980px) {
  .detail-layout {
    grid-template-columns: 1fr;
  }

  .bom-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 680px) {
  .page-heading,
  .section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .heading-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .identity-section {
    grid-template-columns: 1fr;
  }

  .product-media {
    max-width: 220px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .version-row {
    grid-template-columns: 1fr;
  }
}
</style>
